import React, { useMemo } from 'react';
import DashboardCard from './DashboardCard';
import type { DailyCostPoint } from './types';

const CHART_WIDTH = 480;
const CHART_HEIGHT = 150;
const PADDING = { top: 8, right: 10, bottom: 26, left: 45 };

interface LabourCostLineChartProps {
  data: DailyCostPoint[];
  loading?: boolean;
}

const LabourCostLineChart: React.FC<LabourCostLineChartProps> = ({ data, loading }) => {
  const chart = useMemo(() => {
    const innerW = CHART_WIDTH - PADDING.left - PADDING.right;
    const innerH = CHART_HEIGHT - PADDING.top - PADDING.bottom;
    const maxVal = Math.max(1, ...data.flatMap((d) => [d.planned, d.actual]));
    const maxY = Math.ceil(maxVal / 1000) * 1000 || 1000;
    const toX = (index: number) =>
      PADDING.left + (data.length <= 1 ? innerW / 2 : (index / (data.length - 1)) * innerW);
    const toY = (value: number) => PADDING.top + innerH - (value / maxY) * innerH;
    const yTicks = [0, maxY * 0.33, maxY * 0.66, maxY].map((v) => Math.round(v));

    return {
      plannedPoints: data.map((d, i) => `${toX(i)},${toY(d.planned)}`).join(' '),
      actualPoints: data.map((d, i) => `${toX(i)},${toY(d.actual)}`).join(' '),
      yTicks,
      toX,
      toY,
    };
  }, [data]);

  if (loading) {
    return (
      <DashboardCard className="h-full" title="Planned vs Actual Labour Cost">
        <div className="px-4 pb-4 h-36 animate-pulse bg-gray-50 mx-4 mb-4 rounded-lg" />
      </DashboardCard>
    );
  }

  return (
      <DashboardCard className="h-full w-full flex flex-col" title="Planned vs Actual Labour Cost">
      <div className="px-4 pb-3 flex-1 flex flex-col justify-center">
        <div className="flex items-center gap-4 mb-2 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-5 border-t-2 border-dashed border-blue-400" />
            Planned Cost
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-5 border-t-2 border-blue-600" />
            Actual Cost
          </span>
        </div>

        {data.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No labour cost data for this week.</p>
        ) : (
          <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} className="w-full max-h-[150px]">
            {chart.yTicks.map((tick) => {
              const y = chart.toY(tick);
              return (
                <g key={tick}>
                  <line
                    x1={PADDING.left}
                    y1={y}
                    x2={CHART_WIDTH - PADDING.right}
                    y2={y}
                    stroke="#F3F4F6"
                    strokeWidth={1}
                  />
                  <text x={PADDING.left - 8} y={y + 4} textAnchor="end" fontSize={10} fill="#9CA3AF">
                    {tick === 0 ? '€0' : `€${Math.round(tick / 1000)}K`}
                  </text>
                </g>
              );
            })}

            {chart.plannedPoints && (
              <polyline
                points={chart.plannedPoints}
                fill="none"
                stroke="#60A5FA"
                strokeWidth={2}
                strokeDasharray="6 4"
              />
            )}
            {chart.actualPoints && (
              <polyline points={chart.actualPoints} fill="none" stroke="#2563EB" strokeWidth={2.5} />
            )}

            {data.map((d, i) => (
              <g key={d.date}>
                <circle cx={chart.toX(i)} cy={chart.toY(d.planned)} r={3} fill="#60A5FA" />
                <circle cx={chart.toX(i)} cy={chart.toY(d.actual)} r={3.5} fill="#2563EB" />
                <text
                  x={chart.toX(i)}
                  y={CHART_HEIGHT - 6}
                  textAnchor="middle"
                  fontSize={9}
                  fill="#9CA3AF"
                >
                  {d.dateLabel}
                </text>
              </g>
            ))}
          </svg>
        )}
      </div>
    </DashboardCard>
  );
};

export default LabourCostLineChart;
